<script setup lang="ts" generic="TValue extends string | number = string | number">
/**
 * UI-kit component
 * Category: Selects
 * Name: 'AppSelectSearchable'
 * FIGMA: https://www.figma.com/design/oRsJUOAucnhxx5aeqy4TRO/APP-admin?node-id=16127-221763
 */
import type { AppSelectSearchableProps } from '../types/interfaces'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { faXmark } from '@fortawesome/pro-regular-svg-icons'

import { AppText } from '@shared/ui/typography'
import { AppTooltip } from '@shared/ui/tooltips'
import { AppIcon } from '@shared/ui/icons'
import {
  AppDropdown,
  AppDropdownItem,
  AppDropdownLoader,
} from '@shared/ui/dropdowns'
import { BtnSecondary } from '@shared/ui/buttons'

import { useAppSelectClasses } from '../../app-select'
import { useAppSelectSearchable } from '../composables/use-app-select-searchable.hook'

defineOptions({
  name: 'AppSelectSearchable',
})

const props = withDefaults(defineProps<AppSelectSearchableProps<TValue>>(), {
  placeholder: '',
  label: undefined,
  optionalText: undefined,
  required: false,
  state: 'static',
  disabled: false,
  message: undefined,
  prependIcon: undefined,
  panelMinWidth: undefined,
  // search
  selectedOption: undefined,
  noResultsText: 'No results',
  loading: false,
  // pagination
  infinite: false,
  hasMore: false,
})

/**
 * `@search` is emitted on:
 *   - typing >= `minSearchLength` chars (debounced by `debounceMs`)
 *   - clearing the input via X / backspace → immediate `@search('')`
 *   - dropdown close (Esc / Tab / click-outside / option select) when the
 *     query was non-empty → immediate `@search('')`. Use `AbortController`
 *     in your fetcher to cancel any in-flight request from the previous query.
 *
 * `@load-more` is emitted only on explicit "Show more" button click and
 * carries the current query (or '' in browse mode).
 */
const emit = defineEmits<{
  open: []
  close: []
  search: [query: string]
  'load-more': [query: string]
}>()

// `update:modelValue` is auto-emitted by `defineModel`.
const modelValue = defineModel<TValue>()

defineSlots<{
  'prepend-icon'?: () => unknown
  option?: (scope: {
    option: AppSelectOption<TValue>
    selected: boolean
    disabled: boolean
  }) => unknown
}>()

const {
  triggerRef,
  inputRef,
  panelRef,
  isOpen,
  panelId,
  labelId,
  floatingStyles,
  isDisabled,
  isError,
  isSuccess,
  isHighlighted,
  displayedOptions,
  inputValue,
  inputPlaceholder,
  tooltipText,
  showClearButton,
  showNoResults,
  isClearDisabled,
  isLoadMoreDisabled,
  showClearValueButton,
  focusInput,
  handleChevronClick,
  handleClearValueClick,
  handleInputFocus,
  handleInputBlur,
  handleClearClick,
  handleLoadMoreClick,
  handleTriggerKeydown,
  handleSelect,
} = useAppSelectSearchable<TValue>({
  modelValue,
  props,
  onOpen: () => emit('open'),
  onClose: () => emit('close'),
  onSearch: (query) => emit('search', query),
  onLoadMore: (query) => emit('load-more', query),
})

// Default-skin visuals — view layer, not the headless core.
const { rootClasses, triggerClasses, messageColor, chevronIcon } = useAppSelectClasses({
  isOpen,
  isDisabled,
  isError,
  isSuccess,
})
</script>

<template>
  <div :class="rootClasses">
    <div
      v-if="label"
      class="app-select__label-row"
    >
      <AppText
        :id="labelId"
        name="body2"
        color="slate-900"
        class="app-select__label"
      >
        {{ label }}<span
          v-if="required"
          class="app-select__required"
        >*</span>
      </AppText>
      <AppText
        v-if="optionalText"
        name="body2"
        color="slate-400"
        class="app-select__optional"
      >
        {{ optionalText }}
      </AppText>
    </div>

    <AppTooltip
      :text="tooltipText"
      placement="top"
    >
      <div
        ref="triggerRef"
        :class="triggerClasses"
        role="combobox"
        :aria-expanded="isOpen"
        aria-haspopup="listbox"
        :aria-controls="panelId"
        @click="focusInput"
      >
        <span
          v-if="$slots['prepend-icon'] || prependIcon"
          class="app-select__prepend"
        >
          <slot name="prepend-icon">
            <AppIcon
              v-if="prependIcon"
              :icon="prependIcon"
            />
          </slot>
        </span>

        <input
          ref="inputRef"
          v-model="inputValue"
          type="text"
          class="app-select__input"
          :placeholder="inputPlaceholder"
          :disabled="isDisabled"
          :aria-labelledby="label ? labelId : undefined"
          :aria-required="required || undefined"
          :aria-invalid="isError || undefined"
          @focus="handleInputFocus"
          @blur="handleInputBlur"
          @keydown="handleTriggerKeydown"
        >

        <button
          v-if="showClearButton"
          type="button"
          class="app-select__clear-btn"
          :disabled="isClearDisabled"
          tabindex="-1"
          aria-label="Clear search"
          @mousedown.prevent
          @click.stop="handleClearClick"
        >
          <AppIcon :icon="faXmark" />
        </button>
        <button
          v-else-if="showClearValueButton"
          type="button"
          class="app-select__clear-btn"
          :disabled="isDisabled"
          tabindex="-1"
          aria-label="Clear selection"
          @mousedown.prevent
          @click.stop="handleClearValueClick"
        >
          <AppIcon :icon="faXmark" />
        </button>
        <button
          v-else
          type="button"
          class="app-select__chevron-btn"
          :disabled="isDisabled"
          tabindex="-1"
          aria-label="Toggle dropdown"
          @mousedown.prevent
          @click.stop="handleChevronClick"
        >
          <AppIcon :icon="chevronIcon" />
        </button>
      </div>
    </AppTooltip>

    <AppText
      v-if="message"
      name="body2"
      :color="messageColor"
      class="app-select__message"
    >
      {{ message }}
    </AppText>

    <Teleport to="body">
      <div
        v-if="isOpen"
        :id="panelId"
        ref="panelRef"
        role="listbox"
        class="app-select__panel"
        :style="floatingStyles"
      >
        <AppDropdown>
          <!-- Initial / search-replace loading: panel is open but options not
               yet fetched. Show the loader as the panel's main content so the
               dropdown doesn't appear blank while the request is in flight. -->
          <AppDropdownLoader v-if="loading && displayedOptions.length === 0" />

          <template v-else>
            <div
              v-for="(option, index) in displayedOptions"
              :key="String(option.value)"
              :data-app-select-option="index"
              :class="[
                'app-select__option',
                {
                  'app-select__option--highlighted': isHighlighted(index),
                  'app-select__option--selected': option.value === modelValue,
                  'app-select__option--disabled': option.disabled === true,
                },
              ]"
              role="option"
              :aria-selected="option.value === modelValue"
              :aria-disabled="option.disabled || undefined"
              tabindex="-1"
              @click="handleSelect(option as AppSelectOption<TValue>)"
            >
              <slot
                name="option"
                :option="option"
                :selected="option.value === modelValue"
                :disabled="option.disabled === true"
              >
                <AppDropdownItem
                  tag="div"
                  :text="option.text"
                  :selected="option.value === modelValue"
                  :disabled="option.disabled"
                  tabindex="-1"
                />
              </slot>
            </div>

            <div
              v-if="showNoResults"
              class="app-select__no-results"
            >
              <AppText
                name="body1"
                color="slate-500"
              >
                {{ noResultsText }}
              </AppText>
            </div>
          </template>

          <template
            v-if="infinite && hasMore"
            #footer
          >
            <div class="app-select__footer">
              <AppDropdownLoader v-if="loading" />
              <BtnSecondary
                v-else
                text="Load more"
                :disabled="isLoadMoreDisabled"
                @click="handleLoadMoreClick"
              />
            </div>
          </template>
        </AppDropdown>
      </div>
    </Teleport>
  </div>
</template>

<style scoped lang="scss" src="../styles/_app-select-searchable.scss" />
